import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { Agency } from '../agency';
import { Fire, FireType, FireCause, FireLocation } from '../fire';
import { Site } from '../site';
import { MessageService } from '../message.service';

@Injectable({
  providedIn: 'root'
})
export class FireDataService {

  private path = (location.hostname === 'localhost')  ? 'https://dev5.bats.admin.ufl.edu' : 'https://' + location.hostname;

  private agencyAPI = this.path + '/' + window.location.pathname.split('/')[1] +'/api/agencyList?TYPE=FIRE';
  private causeAPI = this.path + '/' + window.location.pathname.split('/')[1] + '/api/fireCauseList';
  private clearFireAPI = this.path + '/' + window.location.pathname.split('/')[1] +'/api/clearFireLogs';
  private fireAPI = this.path + '/' + window.location.pathname.split('/')[1] + '/api/fire';
  private fireListAPI = `${this.path}/${window.location.pathname.split('/')[1]}/api/fireList${(window.location.pathname.split('/')[1] == 'clery') ? '60' :''}`
  private fireListAll = `${this.path}/${window.location.pathname.split('/')[1]}/api/fireListAll`;
  private fireLocationsAPI = this.path + '/' + window.location.pathname.split('/')[1] + '/api/fireLocationList';
  private siteAPI = this.path + '/' + window.location.pathname.split('/')[1] + '/api/fireSiteList';
  private typeAPI = this.path + '/' + window.location.pathname.split('/')[1] + '/api/fireTypeList';

  httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' })
  };

  constructor(
    private http: HttpClient,
    private messageService: MessageService) {}

  /** Clear fire logs from the server */
  clearFires(data) {
    return this.http.put(this.clearFireAPI, data, this.httpOptions).pipe(
      catchError(this.handleError<Fire>('clearFires'))
    );
  }

  deleteFire(id) {
    return this.http.delete(this.fireAPI + '/' + id, this.httpOptions).pipe(
      catchError(this.handleError<Fire>('delete fire'))
    );
  }

    /** GET crimes to be cleared list from the server */
    getAgencies(): Observable<Agency[]> {
      return this.http.get<Agency[]>(this.agencyAPI)
      .pipe(
        tap(_ => this.log('fetched agencies')),
        catchError(this.handleError<Agency[]>('getAgencies', []))
      );
    }

  /** GET fire cause list from the server */
  getCauses(): Observable<FireCause[]> {
    return this.http.get<FireCause[]>(this.causeAPI)
    .pipe(
      tap(_ => this.log('fetched causes')),
      catchError(this.handleError<FireCause[]>('getCauses', []))
    );
  }

  /** GET fire count from the server */
  getFiresToClear(data): Observable<object> {
    return this.http.get<object>(this.clearFireAPI + '?sitecode=' + data.SITECODE + '&delete_date=' + data.DELETE_DATE)
    .pipe(
      tap(_ => this.log('fetched fires to clear')),
      catchError(this.handleError<object>('getFiresToClear'))
    );
  }

  /** GET fires from the server */
  getFires(maxRowId: number): Observable<Fire[]> {
    return this.http.get<Fire[]>(this.fireListAPI + '?id=' + maxRowId)
    .pipe(
      //tap(_ => this.log('fetched fires')),
      //tap(e => console.log(e)),
      catchError(this.handleError<Fire[]>('getFires', []))
    );
  }


    /** GET All fires from the server */
    getFiresAll(): Observable<Fire[]> {
      return this.http.get<Fire[]>(this.fireListAll)
      .pipe(
        //tap(_ => this.log('fetched fires')),
        //tap(e => console.log(e)),
        catchError(this.handleError<Fire[]>('getFires', []))
      );
    }
    /** GET firelocation list from the server */
    getFireLocations(siteCode): Observable<FireLocation[]> {
      return this.http.get<FireLocation[]>(this.fireLocationsAPI + '?sitecode=' + siteCode)
      .pipe(
        tap(_ => this.log('fetched fire locations')),
        catchError(this.handleError<FireLocation[]>('getFireLocs'))
      );
    }

  /** GET agency list from the server */
  getSites(): Observable<Site[]> {
    return this.http.get<Site[]>(this.siteAPI)
    .pipe(
      tap(_ => this.log('fetched sites')),
      catchError(this.handleError<Site[]>('getSites', []))
    );
  }

  /** GET agency list from the server */
  getTypes(): Observable<FireType[]> {
    return this.http.get<FireType[]>(this.typeAPI)
    .pipe(
      tap(_ => this.log('fetched fire types')),
      catchError(this.handleError<FireType[]>('getTypes', []))
    );
  }

    /** Add new fire to database */
  addFire(fire: Fire): Observable<Fire> {
    return this.http.post<Fire>(this.fireAPI, fire, this.httpOptions).pipe(
      tap((newFire: Fire) => this.log(`Added fire w/ id=${newFire.ID}`)),
      catchError(this.handleError<Fire>('addFire'))
    );
  }

  /** Save fire to database */
  saveFire(fire: Fire): Observable<Fire> {
    return this.http.put<Fire>(this.fireAPI, fire, this.httpOptions).pipe(
      tap((newFire: Fire) => this.log(`Saved fire w/ id=${newFire.ID}`)),
      catchError(this.handleError<Fire>('saveFire'))
    );
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {

      // TODO: send the error to remote logging infrastructure
      console.error(error); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  /** Log a HeroService message with the MessageService */
  private log(message: string) {
    this.messageService.add('Fire Log Service: ${message}');
  }
}
